// The Work Centre split of a priced line list — 1.0 showed this as a table under
// the quote lines; 2.0 shows it in the Work Centre column's own filter (Robert
// 2026-08-03: "a dropdown ... when selected it only shows that work centre as
// well as totals only being that work centre").
//
// Pure on purpose. The money is the point, so it belongs somewhere a test can
// reach it rather than inside a component's useMemo.
//
// HEADER ROWS COUNT. This looks wrong and is not. `is_header` reads like a
// grouping row that would double-count if included, and that is how the printed
// summary treats it (quoteReport.buildSummaries groups over `detail`). The data
// says otherwise: 9,982 of 28,590 header rows in dev carry money, they have NO
// child rows hanging off them, and on claim 73957 $611,144 of a $612,142 quote
// sits on headers with $998 on detail lines. They are priced lines that happen
// to be flagged as headers. Excluding them would have reported that quote as
// $998, and the per-centre totals would not have added up to the grid's own
// Totals row — which sums every row and always has.
// (The printed summary under-reports these same quotes. Separate defect.)

// Lines with no work centre still hold money, so they get a bucket rather than
// vanishing — the print summary names it the same thing.
export const NO_WORK_CENTRE = "none";
export const NO_WORK_CENTRE_LABEL = "(No work centre)";

// Keys are STRINGS throughout: they come back from a <select> as strings, and
// `work_centre_id` arrives from pg as a string on some paths and a number on
// others. Normalising once here is what stops "12" !== 12 filtering everything
// out.
export function workCentreKey(line) {
  return line.work_centre_id == null || line.work_centre_id === ""
    ? NO_WORK_CENTRE
    : String(line.work_centre_id);
}

export function sumLines(lines) {
  return lines.reduce(
    (a, it) => {
      a.gross += Number(it.gross_value || 0);
      a.margin += Number(it.margin_value || 0);
      a.net += Number(it.net_value || 0);
      return a;
    },
    { gross: 0, margin: 0, net: 0 }
  );
}

// One row per work centre present, with its own totals. Alphabetical, with the
// no-work-centre bucket last so it reads as the leftovers rather than as a
// centre called "(".
//
// Buckets holding NO money at all are dropped: a quote's plain grouping headers
// are unassigned and unpriced, and without this every quote would offer a
// "(No work centre) — $0.00" option that filters to a handful of captions.
// Dropping them cannot move a total, because they are zero.
export function workCentreBreakdown(items) {
  const byKey = new Map();
  for (const line of items) {
    const key = workCentreKey(line);
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        name: key === NO_WORK_CENTRE ? NO_WORK_CENTRE_LABEL : line.work_centre_name || `#${key}`,
        lines: [],
      });
    }
    byKey.get(key).lines.push(line);
  }
  return [...byKey.values()]
    .map((g) => ({ key: g.key, name: g.name, count: g.lines.length, ...sumLines(g.lines) }))
    .filter((g) => g.gross !== 0 || g.margin !== 0 || g.net !== 0)
    .sort((a, b) => {
      if (a.key === NO_WORK_CENTRE) return 1;
      if (b.key === NO_WORK_CENTRE) return -1;
      return a.name.localeCompare(b.name);
    });
}

// Rows to show for a filter. "" = everything, untouched — the unfiltered grid
// must behave exactly as it did before, same rows in the same order.
export function filterByWorkCentre(items, key) {
  if (!key) return items;
  return items.filter((it) => workCentreKey(it) === key);
}

// Job profitability (KR 2026-08-20, formula confirmed by Robert): what share
// of the sell price is NOT ordinary-line cost. The auto fee lines (line_kind
// pg/jmf/profit) count as PROFIT — they carry no trade cost — while
// hand-entered fee lines on pre-upgrade jobs are ordinary lines and count as
// cost, which is exactly why the office is moving to the auto lines.
// Internal display only (the grid footer); normally ~53%, which is the target.
export function jobProfitability(lines = []) {
  const sell = lines.reduce((s, l) => s + Number(l.net_value || 0), 0);
  if (!(sell > 0)) return null;
  const ordinaryCost = lines.reduce(
    (s, l) => s + (l.line_kind ? 0 : Number(l.gross_value || 0)),
    0
  );
  return +(((sell - ordinaryCost) / sell) * 100).toFixed(1);
}

// The SAME profit as a percentage ON COST — the markup dialect. Karen S added
// the fee badges (17.8 + 17.71 + 18 ≈ 53) and the footer answered 34.8: both
// were right, one number is markup-on-cost and the other margin-of-sell, so
// the footer now speaks both, labelled the way every margin calculator labels
// them (Robert 2026-08-21). Null when there is no ordinary cost to mark up
// (an all-fee quote divides by zero).
export function jobMarkup(lines = []) {
  const sell = lines.reduce((s, l) => s + Number(l.net_value || 0), 0);
  const ordinaryCost = lines.reduce(
    (s, l) => s + (l.line_kind ? 0 : Number(l.gross_value || 0)),
    0
  );
  if (!(sell > 0) || !(ordinaryCost > 0)) return null;
  return +(((sell - ordinaryCost) / ordinaryCost) * 100).toFixed(1);
}
